import path from "path";

import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import {
    FileStreamResult,
    ProxyGetRequestToResult,
    StreamReturnResult,
} from "../../../Commons/HttpServer/AttributeRouting/Results/StreamResultHandler";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";

import { ClientJsDevServer } from "./DevServers/ClientJsDevServer";

export interface BuildServerClientJsControllerOptions {
    frontEndDevMode: boolean;
    devServerUrl: string;
}

const defaultOptions: BuildServerClientJsControllerOptions = {
    frontEndDevMode: false,
    devServerUrl: "https://localhost:8081",
};

export class BuildServerClientJsController extends SingletonController {
    private readonly engineDirectory: string;
    private readonly options: Partial<BuildServerClientJsControllerOptions>;
    private readonly disableClientOverride: boolean;
    private devServer?: ClientJsDevServer;
    private readonly buildDebugOnly: boolean;

    public constructor(
        engineDirectory: string,
        buildDebugOnly: boolean,
        disableClientOverride: boolean,
        options: Partial<BuildServerClientJsControllerOptions>
    ) {
        super();
        this.disableClientOverride = disableClientOverride;
        this.options = { ...defaultOptions, ...options };
        this.engineDirectory = engineDirectory;
        this.buildDebugOnly = buildDebugOnly;
    }

    @httpGet("/public/scripts/client.js", httpReturn.StreamWithMime("application/javascript"))
    public async getClientJsScript(): Promise<StreamReturnResult> {
        if (this.disableClientOverride) {
            return this.getClientJsScriptOriginal();
        }
        if (this.options.frontEndDevMode) {
            return new ProxyGetRequestToResult(`${this.options.devServerUrl}/forms-helper/enhancedClient.js`);
        } else {
            return new FileStreamResult(
                path.join(__dirname, "..", "..", "..", "LocalServerFrontEndDist", "enhancedClient.js")
            );
        }
    }

    @httpGet("/public/scripts/client.js.map", httpReturn.StreamWithMime("application/json"))
    public async getClientJsMapScript(): Promise<StreamReturnResult> {
        return (await this.getOrCreateDevServer()).getFileStream(`static/public/scripts/client.js.map`);
    }

    @httpGet("/public/scripts/client.min.js", httpReturn.StreamWithMime("application/javascript"))
    public async getClientMinJsScript(): Promise<StreamReturnResult> {
        if (this.disableClientOverride) {
            return this.getClientMinJsScriptOriginal();
        }
        if (this.options.frontEndDevMode) {
            return new ProxyGetRequestToResult(`${this.options.devServerUrl}/forms-helper/enhancedClient.js`);
        } else {
            return new FileStreamResult(
                path.join(__dirname, "..", "..", "..", "LocalServerFrontEndDist", "enhancedClient.js")
            );
        }
    }

    @httpGet("/public/scripts/client.min.js.map", httpReturn.StreamWithMime("application/json"))
    public async getClientMinJsMapScript(): Promise<StreamReturnResult> {
        return (await this.getOrCreateDevServer()).getFileStream(`static/public/scripts/client.min.js.map`);
    }

    @httpGet("/public/scripts/client-original.js", httpReturn.StreamWithMime("application/javascript"))
    public async getClientJsScriptOriginal(): Promise<NodeJS.ReadableStream> {
        return (await this.getOrCreateDevServer()).getClientJsContentAsStream(false);
    }

    @httpGet("/public/scripts/client-original.min.js", httpReturn.StreamWithMime("application/javascript"))
    public async getClientMinJsScriptOriginal(): Promise<NodeJS.ReadableStream> {
        return (await this.getOrCreateDevServer()).getClientJsContentAsStream(true);
    }

    private async getOrCreateDevServer(): Promise<ClientJsDevServer> {
        if (this.devServer == undefined) {
            this.devServer = new ClientJsDevServer(13001, this.engineDirectory, this.buildDebugOnly);
            await this.devServer.start();
        }
        return this.devServer;
    }
}
